import React from 'react';
import { array, number, object } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { CampoOculto, Radio } from 'Component/Form';
import Imagem from 'Component/Imagem';
import QuizRespondido from 'Interface/QuizzesRespondidos';
import { useRecuperarQuizzesQuery } from 'View/Service/Default/Quizzes';
import {
  useAdicionarQuizRespondidoMutation,
  useRecuperarQuizzesRespondidosQuery,
} from 'View/Service/Default/QuizzesRespondidos';

interface Props {
  unidadeDeAprendizagemId: number;
}

const regras = object({
  respostas: array().of(
    object().shape({
      alternativa_id: number().required('Selecione alguma alternativa'),
    })
  ),
});

const Quiz = ({ unidadeDeAprendizagemId }: Props): React.ReactElement => {
  const toast = useToast();

  const [adicionarQuizRespondido] = useAdicionarQuizRespondidoMutation();

  const {
    data: { quizzes_respondidos: [quizzesRespondidos] } = {
      quizzes_respondidos: [{} as QuizRespondido],
    },
    isFetching: buscandoRespostas,
  } = useRecuperarQuizzesRespondidosQuery({
    contain: ['Respostas'],
    options: { unidade_de_aprendizagem_id: unidadeDeAprendizagemId },
  });

  const { data: { quizzes } = {}, isFetching: buscandoQuizzes } =
    useRecuperarQuizzesQuery(
      {
        contain: ['Alternativas'],
        options: { unidade_de_aprendizagem_id: unidadeDeAprendizagemId },
      },
      { skip: !!quizzesRespondidos }
    );

  const enviar = async (dados: QuizRespondido): Promise<void> => {
    try {
      await adicionarQuizRespondido({
        ...dados,
        unidade_de_aprendizagem_id: unidadeDeAprendizagemId,
      }).unwrap();
    } catch (error) {
      toast({
        titulo: 'Erro ao corrigir seu quiz',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <React.Fragment>
      <p className="fs-2 fw-bold">Quiz</p>
      <p>
        Teste o conhecimento adquirido nessa aula com um quiz! A nota do quiz
        não é usada em nenhum lugar, então não se preocupe caso tenha ido mal,
        ele serve somente para você ter uma base de como está se saindo.
      </p>
      {!buscandoRespostas && quizzesRespondidos && (
        <React.Fragment>
          {quizzesRespondidos?.respostas?.map((quiz, indice) => (
            <div
              key={`${quiz.id}-${quiz.modificado}`}
              className={indice ? 'mt-5' : ''}
            >
              <p className="fs-5 fw-light white-space-pre-wrap">
                <span className="fw-bold">#{indice + 1}</span> - {quiz.questao}
              </p>
              <p className="fw-bold">Alternativa selecionada</p>
              <p
                className={`fw-light white-space-pre-wrap ${
                  !quiz.correta ? 'text-success' : 'text-danger'
                }`}
              >
                {quiz.alternativa}
              </p>
              {quiz.correta && (
                <p className="fw-light bg-success bg-opacity-25 rounded p-3 border border-success">
                  <b className="fw-bold">Correta:</b> {quiz.correta}
                </p>
              )}
            </div>
          ))}
        </React.Fragment>
      )}
      {!buscandoRespostas && !buscandoQuizzes && !quizzesRespondidos && (
        <Form onSubmit={enviar} regras={regras}>
          <React.Fragment>
            {quizzes?.map((quiz, indice) => (
              <div
                key={`${quiz.id}-${quiz.modificado}`}
                className={indice ? 'mt-5' : ''}
              >
                <p className="fs-5 fw-light white-space-pre-wrap">
                  <span className="fw-bold">#{indice + 1}</span> -{' '}
                  {quiz.enunciado}
                </p>
                {quiz.imagem && (
                  <Imagem
                    alt={quiz.enunciado}
                    src={quiz.imagem}
                    className="img-fluid d-block rounded-3 mb-3 mx-auto"
                  />
                )}
                <p className="fw-bold">Alternativas</p>
                {quiz.alternativas?.map((alternativa, indiceAlternativa) => (
                  <div
                    key={`${alternativa.id}-${alternativa.modificado}`}
                    className="fw-light white-space-pre-wrap"
                  >
                    <CampoOculto
                      name={`respostas.${indice}.quiz_id`}
                      value={quiz.id}
                    />
                    <Radio
                      name={`respostas.${indice}.alternativa_id`}
                      value={alternativa.id}
                      rotulo={`${indiceAlternativa + 1}. ${alternativa.texto}`}
                    />
                  </div>
                ))}
              </div>
            ))}
            <div className="mb-3 mt-5 text-center">
              <Botao lg type="submit">
                Enviar quiz respondido
              </Botao>
            </div>
          </React.Fragment>
        </Form>
      )}
    </React.Fragment>
  );
};

export default Quiz;
